import {
  Box,
  Heading,
  Image,
  Text,
  Flex,
  Spinner,
  VStack,
  HStack,
  Link as ChakraLink,
  useToast,
  Button,
  Collapse,
  Badge,
} from "@chakra-ui/react";
import { Table, Thead, Tbody, Tr, Th, Td } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ChevronDown, ChevronUp } from "lucide-react";

import PaymentCheckoutTest from "./PaymentCheckoutTest";
import OrderPricingBreakdownCard, {
  PricingBreakdown,
} from "./OrderPricingBreakdownCard";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";

type OrderItem = {
  _id?: string;
  title?: string;
  description?: string;
  category?: string;
  imageUrl?: string;
  price?: number;
  quantity?: number;
  selectedVariants?: Array<{
    attributeName?: string;
    name?: string;
    label?: string;
    value?: string;
  }>;
  variantSummary?: string;
};

type OrderDetails = {
  _id: string;
  orderId?: string;
  userId?: string;
  email?: string;
  phoneNumber?: string;

  items: OrderItem[];
  total: number;
  currency?: string;

  pricingBreakdown?: PricingBreakdown;

  fromAddress?: string;
  toAddress?: string;

  fromLocation?: {
    lat?: number;
    lng?: number;
  };

  toLocation?: {
    lat?: number;
    lng?: number;
  };

  deliveryInstructions?: string;
  pickupAddress?: string;
  pickupWarehouseId?: string;
  pickupWarehouseName?: string;
  pickupWarehouseAddress?: string;
  checkoutMode?: "delivery" | "pickup";

  payment?: {
    method?: string;
    provider?: string;
    status?: string;
    chargeId?: string;
    reference?: string;
    providerPaymentId?: string;
    providerCheckoutId?: string;
  };

  chargeId?: string;
  reference?: string;
  paymentVerifiedAt?: string;

  status?: string;
  channel?: string;
  customerLocation?: {
    address?: string;
    lat?: number;
    lng?: number;
  };
  deliveryLocation?: {
    address?: string;
    lat?: number;
    lng?: number;
  };

  createdAt?: string;
  updatedAt?: string;
};

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const cardsBorderRadii ="8px";

const getVariantSummary = (item: OrderItem) => {
  if (item?.variantSummary) return item.variantSummary;

  if (
    !Array.isArray(item?.selectedVariants) ||
    item.selectedVariants.length === 0
  ) {
    return "";
  }

  return item.selectedVariants
    .map((variant) => {
      const name = variant.attributeName || variant.name || "Option";
      const value = variant.label || variant.value || "";
      return `${name}: ${value}`;
    })
    .join(" • ");
};

const getSafeItemsTotal = (items: OrderItem[]) => {
  return items.reduce((sum, item) => {
    const price = Number(item?.price || 0);
    const qty = Number(item?.quantity || 0);
    return sum + price * qty;
  }, 0);
};

const getFulfilmentType = (
  order: OrderDetails | null,
): "delivery" | "pickup" => {
  if (!order) return "delivery";

  if (order.checkoutMode === "pickup") return "pickup";
  if (order.checkoutMode === "delivery") return "delivery";

  if (
    order.pickupAddress ||
    order.customerLocation?.address ||
    order.pickupWarehouseId
  ) {
    return "pickup";
  }

  return "delivery";
};

const isNumericCoordinatePair = (value?: string | null) => {
  if (!value) return false;

  const normalized = String(value).trim();

  return /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(normalized);
};

const parseCoordinatePair = (value?: string | null) => {
  if (!isNumericCoordinatePair(value)) return null;

  const [latRaw, lngRaw] = String(value).split(",");

  const lat = Number(latRaw.trim());
  const lng = Number(lngRaw.trim());

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return { lat, lng };
};

const getMapCoordinates = (
  order: OrderDetails | null,
  fulfilmentType: "delivery" | "pickup",
) => {
  if (!order) return null;

  if (fulfilmentType === "delivery") {
    const structuredLat = order.deliveryLocation?.lat ?? order.toLocation?.lat;
    const structuredLng = order.deliveryLocation?.lng ?? order.toLocation?.lng;

    if (
      typeof structuredLat === "number" &&
      typeof structuredLng === "number"
    ) {
      return { lat: structuredLat, lng: structuredLng };
    }

    return parseCoordinatePair(order.toAddress);
  }

  const structuredLat = order.customerLocation?.lat;
  const structuredLng = order.customerLocation?.lng;

  if (typeof structuredLat === "number" && typeof structuredLng === "number") {
    return { lat: structuredLat, lng: structuredLng };
  }

  return (
    parseCoordinatePair(order.customerLocation?.address) ||
    parseCoordinatePair(order.pickupAddress)
  );
};

const buildMapEmbedUrl = (lat: number, lng: number) =>
  `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;

const reverseGeocodeLatLng = async (
  lat: number,
  lng: number,
): Promise<string> => {
  try {
    const response = await axios.get(
      "https://nominatim.openstreetmap.org/reverse",
      {
        params: {
          lat,
          lon: lng,
          format: "jsonv2",
        },
        headers: {
          Accept: "application/json",
        },
      },
    );

    const data = response.data;

    return data?.display_name || data?.name || `${lat}, ${lng}`;
  } catch (error) {
    console.error("Reverse geocoding failed:", error);
    return `${lat}, ${lng}`;
  }
};

const prettifyValue = (value?: string) => {
  if (!value) return "—";
  return value.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
};

const normalizePaymentStatus = (order: OrderDetails | null) => {
  if (!order) return "pending";

  const paymentStatus = String(order.payment?.status || "")
    .trim()
    .toLowerCase();
  const orderStatus = String(order.status || "")
    .trim()
    .toLowerCase();

  if (paymentStatus) return paymentStatus;
  if (orderStatus === "paid") return "paid";

  return "pending";
};

const isPaidStatus = (status?: string) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  return normalized === "paid";
};

const WhatsappCheckoutPage = () => {
  const { orderId = "" } = useParams<{ orderId: string }>();
  const toast = useToast();
  const { theme } = useCustomTheme();

  const isDark = theme.name === "dark";

  const pageText = theme.text;
  const mutedText = theme.textMuted;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const borderColor = theme.border;
  const dividerColor = theme.divider;
  const panelShadow = theme.shadowMd;

  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [resolvedLocationName, setResolvedLocationName] = useState("");
  const [resolvingLocationName, setResolvingLocationName] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  const fulfilmentType = useMemo(
    () => getFulfilmentType(orderDetails),
    [orderDetails],
  );

  const payableAmount = useMemo(() => {
    if (!orderDetails) return 0;
    const total = Number(orderDetails.total || 0);
    return total > 0 ? total : getSafeItemsTotal(orderDetails.items || []);
  }, [orderDetails]);

  const paymentStatus = useMemo(
    () => normalizePaymentStatus(orderDetails),
    [orderDetails],
  );

  const isAlreadyPaid = useMemo(
    () => isPaidStatus(paymentStatus),
    [paymentStatus],
  );

  const mapCoordinates = useMemo(
    () => getMapCoordinates(orderDetails, fulfilmentType),
    [orderDetails, fulfilmentType],
  );

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const response = await axios.get(
          `${API_BASE_URL}/api/v1/orders/${orderId}`,
        );

        const payload = response.data?.data || response.data;
        setOrderDetails(payload);
      } catch (error) {
        console.error("Failed to fetch order details:", error);
        toast({
          title: "Failed to load order details",
          status: "error",
          duration: 3000,
        });
        setOrderDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, toast]);

  useEffect(() => {
    if (!mapCoordinates) {
      setResolvedLocationName("");
      setResolvingLocationName(false);
      return;
    }

    let isMounted = true;

    const run = async () => {
      setResolvingLocationName(true);
      const name = await reverseGeocodeLatLng(
        mapCoordinates.lat,
        mapCoordinates.lng,
      );

      if (isMounted) {
        setResolvedLocationName(name);
        setResolvingLocationName(false);
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [mapCoordinates]);

  const handleUseExistingOrder = async (): Promise<string | null> => {
    if (!orderDetails?._id) {
      toast({
        title: "Order details not found",
        status: "warning",
        duration: 3000,
      });
      return null;
    }

    return orderDetails._id;
  };

  if (loading) {
    return (
      <Box
        p={6}
        borderRadius={cardsBorderRadii}
        bg={cardBg}
        border={`1px solid ${borderColor}`}
        boxShadow={panelShadow}
        m={{ base: 3, md: 6 }}
      >
        <HStack spacing={3}>
          <GlobalSpinner />
          <Text color={pageText}>Loading order details...</Text>
        </HStack>
      </Box>
    );
  }

  if (!orderDetails) {
    return (
      <Box
        p={6}
        borderRadius={cardsBorderRadii}
        bg={cardBg}
        border={`1px solid ${borderColor}`}
        boxShadow={panelShadow}
        m={{ base: 3, md: 6 }}
      >
        <Heading size="md" color={pageText} mb={2}>
          Order not found
        </Heading>
        <Text color={mutedText}>
          We could not load the order details for this checkout page.
        </Text>
      </Box>
    );
  }

  return (
    <Flex
      direction={{ base: "column", md: "row" }}
      gap={6}
      color={pageText}
      p={{ base: 3, md: 6 }}
    >
      <Box
        w="full"
        bg={cardBg}
        p={5}
        borderRadius={cardsBorderRadii}
        boxShadow={panelShadow}
        border={`1px solid ${borderColor}`}
        backdropFilter={isDark ? "blur(8px)" : undefined}
      >
        <Heading size="lg" color={pageText} mb={4}>
          Checkout
        </Heading>

        <Button
          w="full"
          mb={4}
          onClick={() => setShowOrderDetails((prev) => !prev)}
          bg={softBg}
          color={pageText}
          border={`1px solid ${borderColor}`}
          _hover={{ bg: softBg }}
          _active={{ bg: softBg }}
          px={4}
        >
          <Flex w="full" align="center" justify="space-between">
            <Text fontWeight="600">
              {showOrderDetails ? "Hide order details" : "Show order details"}
            </Text>
            {showOrderDetails ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronDown size={18} />
            )}
          </Flex>
        </Button>

        <Collapse in={showOrderDetails} animateOpacity>
          <Box mb={5}>
            <Box
              p={4}
              borderRadius={cardsBorderRadii}
              bg={softBg}
              border={`1px solid ${borderColor}`}
              mb={4}
            >
              <Text color={mutedText} fontSize="sm">
                Payable
              </Text>

              <Text
                mt={1}
                fontWeight="800"
                fontSize="xl"
                color={theme.success || "#38A169"}
              >
                MWK {payableAmount.toFixed(2)}
              </Text>

              <Flex mt={3} gap={2} alignItems="center" wrap="wrap">
                <Text color={mutedText} fontSize="sm">
                  Payment Status:
                </Text>
                <Badge
                  colorScheme={isAlreadyPaid ? "green" : "orange"}
                  borderRadius={cardsBorderRadii}
                  px={3}
                  py={1}
                >
                  {prettifyValue(paymentStatus)}
                </Badge>
              </Flex>

              <VStack align="start" spacing={1} mt={3}>
                <Text color={mutedText} fontSize="sm">
                  Method:{" "}
                  <Text as="span" color={pageText} fontWeight="700">
                    {prettifyValue(orderDetails.payment?.method)}
                  </Text>
                </Text>

                <Text color={mutedText} fontSize="sm">
                  Provider:{" "}
                  <Text as="span" color={pageText} fontWeight="700">
                    {prettifyValue(orderDetails.payment?.provider)}
                  </Text>
                </Text>

                {(orderDetails.payment?.reference ||
                  orderDetails.reference ||
                  orderDetails.chargeId) && (
                  <Text color={mutedText} fontSize="sm">
                    Reference:{" "}
                    <Text as="span" color={pageText} fontWeight="700">
                      {orderDetails.payment?.reference ||
                        orderDetails.reference ||
                        orderDetails.chargeId}
                    </Text>
                  </Text>
                )}
              </VStack>
            </Box>

            <OrderPricingBreakdownCard
              breakdown={orderDetails.pricingBreakdown}
              fallbackTotal={payableAmount}
              title="Payment Breakdown"
            />

            <Heading size="md" color={pageText} mt={5} mb={4}>
              Order Summary
            </Heading>

            <Box
              borderRadius={cardsBorderRadii}
              overflow="hidden"
              border={`1px solid ${borderColor}`}
              bg={softBg}
            >
              <Table size="sm" variant="simple">
                <Thead
                  bg={
                    isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.7)"
                  }
                >
                  <Tr>
                    <Th color={mutedText}>Item</Th>
                    <Th color={mutedText}>Qty</Th>
                    <Th color={mutedText}>Total</Th>
                  </Tr>
                </Thead>

                <Tbody>
                  {(orderDetails.items || []).map((item, index) => (
                    <Tr key={item._id || `${item.title}-${index}`}>
                      <Td borderColor={dividerColor}>
                        <Flex gap={3} align="center">
                          <Image
                            src={item.imageUrl || ""}
                            boxSize="44px"
                            borderRadius={cardsBorderRadii}
                            objectFit="cover"
                            border={`1px solid ${borderColor}`}
                            bg={cardBg}
                          />

                          <Box>
                            <Text color={pageText} fontWeight="600">
                              {item.title || "Untitled item"}
                            </Text>

                            {!!getVariantSummary(item) && (
                              <Text color={mutedText} fontSize="xs" mt={1}>
                                {getVariantSummary(item)}
                              </Text>
                            )}

                            {!!item.description && (
                              <Text
                                color={mutedText}
                                fontSize="xs"
                                mt={1}
                                noOfLines={2}
                              >
                                {item.description}
                              </Text>
                            )}
                          </Box>
                        </Flex>
                      </Td>

                      <Td borderColor={dividerColor}>
                        <Text color={pageText} fontWeight="700">
                          {Number(item.quantity || 0)}
                        </Text>
                      </Td>

                      <Td borderColor={dividerColor}>
                        <Text color={pageText} fontWeight="700">
                          MWK{" "}
                          {(
                            Number(item.price || 0) * Number(item.quantity || 0)
                          ).toFixed(2)}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </Collapse>

        <Box mt={2} mb={6}>
          {isAlreadyPaid ? (
            <Box
              p={4}
              borderRadius={cardsBorderRadii}
              bg={softBg}
              border={`1px solid ${borderColor}`}
            >
              <Heading size="sm" color={theme.success || "#38A169"} mb={2}>
                Payment already received
              </Heading>
              <Text color={mutedText}>
                This order has already been paid. You do not need to pay again.
              </Text>
            </Box>
          ) : (
            <PaymentCheckoutTest
              amount={payableAmount}
              onCreateOrder={handleUseExistingOrder}
            />
          )}
        </Box>

        {mapCoordinates && (
          <Box
            p={4}
            borderRadius={cardsBorderRadii}
            bg={softBg}
            border={`1px solid ${borderColor}`}
            mb={5}
          >
            <VStack align="start" spacing={3} mb={3}>
              <Text
                fontSize="sm"
                color={mutedText}
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="0.04em"
              >
                {fulfilmentType === "delivery"
                  ? "Delivering to"
                  : "Pickup location"}
              </Text>

              <Text color={pageText} fontWeight="700" fontSize="md">
                {resolvingLocationName
                  ? "Resolving address..."
                  : resolvedLocationName ||
                    `${mapCoordinates.lat}, ${mapCoordinates.lng}`}
              </Text>
            </VStack>

            <Box
              borderRadius={cardsBorderRadii}
              overflow="hidden"
              border={`1px solid ${borderColor}`}
              bg={cardBg}
              h={{ base: "220px", md: "280px" }}
            >
              <iframe
                title="Order location map"
                src={buildMapEmbedUrl(mapCoordinates.lat, mapCoordinates.lng)}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>

            <ChakraLink
              href={`https://www.google.com/maps?q=${mapCoordinates.lat},${mapCoordinates.lng}`}
              isExternal
              display="inline-block"
              mt={3}
              color={theme.brand?.electricBlue || theme.primary}
              fontWeight="700"
            >
              Open map pin
            </ChakraLink>
          </Box>
        )}
      </Box>
    </Flex>
  );
};

export default WhatsappCheckoutPage;
